import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { api } from '@/store/api';
import {
  setDirectConnectLinkedWorkspace,
  setSessionCaseInfo,
} from '@/store/slices/callSlice';
import { fetchCustomerForCall } from '@/store/slices/customerSlice';
import { clearCaseInfo } from '@/store/slices/caseSlice';
import { Box, Button, Paper, Typography } from '@gapartners/ui';
import { CustomerInfoHeader } from './customer-info/CustomerInfoHeader';
import { DirectConnectLinkSection } from './customer-info/DirectConnectLinkSection';
import { renderCustomerCardBody } from './customer-info/renderCustomerCardBody';
import { CustomerDetailsSection } from './customer-info/CustomerDetailsSection';
import { ClientSpaceWorkspaceLink } from './ClientSpaceWorkspaceLink';
import { CaseHistoryRow } from './CaseHistoryRow';
import type { CompanySearchResult } from './customer-info/types';

export function CustomerInfoCard() {
  const dispatch = useAppDispatch();
  const contactId = useAppSelector(state => state.call.contactId);
  const isDirectConnect = useAppSelector(state => state.call.isDirectConnect);
  const isAuthenticated =
    useAppSelector(state => state.call.authenticated) ?? false;
  const directConnectLinkedWorkspaceId = useAppSelector(
    state => state.call.directConnectLinkedWorkspaceId
  );
  const directConnectLinkedCompanyName = useAppSelector(
    state => state.call.directConnectLinkedCompanyName
  );
  const callerPhoneNumber = useAppSelector(
    state => state.call.callerPhoneNumber
  );
  const {
    currentCustomer: customerData,
    isLoading,
    error,
  } = useAppSelector(state => state.customer);
  const customerWorkSpaceID = useAppSelector(
    state => state.customer.workSpaceID
  );
  const currentCase = useAppSelector(state => state.case.currentCase);
  const caseHistory = useAppSelector(state => state.call.caseHistory);

  const [caseHistoryExpanded, setCaseHistoryExpanded] = useState(false);
  const [changeWorkspaceExpanded, setChangeWorkspaceExpanded] = useState(false);
  const [companyQuery, setCompanyQuery] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);

  const { t } = useTranslation();
  const [
    triggerSearch,
    { data: searchResults, isFetching: searching, isSuccess: searchSucceeded },
  ] = api.useLazySearchCustomersByCompanyNameQuery();
  const [linkCustomer, { isLoading: linking }] =
    api.useLinkCustomerToCallMutation();

  const handleSearch = () => {
    const q = companyQuery.trim();
    if (!q) return;
    setLinkError(null);
    void triggerSearch({ companyName: q });
  };

  const handleLink = async (workspaceId: number, companyName: string) => {
    if (!contactId) return;
    setLinkError(null);
    try {
      await linkCustomer({
        contactId,
        body: { workspaceId },
      }).unwrap();
      dispatch(
        setDirectConnectLinkedWorkspace({
          workspaceId: String(workspaceId),
          companyName,
        })
      );
      // Clear the previous workspace's case data from ALL layers (global
      // case slice AND per-session copy) so that stale case info cannot be
      // restored later by SessionSwitcher / clearContact / card resyncs.
      // Customer data is overwritten naturally by the fetch below — its
      // isLoading spinner covers the transition so there is no flash.
      dispatch(setSessionCaseInfo({ contactId, caseInfo: null }));
      dispatch(clearCaseInfo());
      await dispatch(
        fetchCustomerForCall({ workSpaceID: String(workspaceId), contactId })
      );
    } catch {
      setLinkError(t('components.contactCenter.directConnectLinkFailed'));
    }
  };

  const directConnectContent = (
    <DirectConnectLinkSection
      t={t}
      companyQuery={companyQuery}
      setCompanyQuery={setCompanyQuery}
      searching={searching}
      linking={linking}
      searchSucceeded={searchSucceeded}
      results={(searchResults?.customers ?? []) as CompanySearchResult[]}
      linkError={linkError}
      onSearch={handleSearch}
      onLink={(organizationId, companyName) => {
        void handleLink(organizationId, companyName);
      }}
    />
  );

  const stateContent = renderCustomerCardBody({
    callerPhoneNumber,
    isDirectConnect,
    isAuthenticated,
    contactId,
    directConnectLinkedWorkspaceId,
    directConnectLinkedCompanyName,
    isLoading,
    error,
    customerExists: Boolean(customerData),
    t,
    directConnectContent,
  });

  if (stateContent) {
    return (
      <Paper
        variant="outlined"
        sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <CustomerInfoHeader t={t} />
        {stateContent}
      </Paper>
    );
  }

  if (!customerData) return null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      <CustomerInfoHeader t={t} />

      <Typography variant="body2" fontWeight={500} color="text.primary">
        <ClientSpaceWorkspaceLink
          workspaceId={customerWorkSpaceID ?? directConnectLinkedWorkspaceId}
          sx={{ fontWeight: 500, color: 'text.primary' }}
        >
          {customerData.companyName}
        </ClientSpaceWorkspaceLink>
      </Typography>

      <CustomerDetailsSection t={t} customerData={customerData} />

      {/* Change Workspace */}
      <Box>
        <Button
          variant="text"
          size="small"
          onClick={() => setChangeWorkspaceExpanded(!changeWorkspaceExpanded)}
          sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
        >
          <Typography variant="body2" fontWeight={600}>
            {t('components.contactCenter.changeWorkspace')}
            {changeWorkspaceExpanded ? ' ▾' : ' ▸'}
          </Typography>
        </Button>
        {changeWorkspaceExpanded && (
          <Box sx={{ mt: 0.5 }}>{directConnectContent}</Box>
        )}
      </Box>

      {/* Active Case Info */}
      {currentCase && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {t('components.contactCenter.activeCase')}: #{currentCase.id}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('components.contactCenter.status')}: {currentCase.status}
          </Typography>
        </Box>
      )}

      {/* Case History */}
      {caseHistory && caseHistory.length > 0 && (
        <Box>
          <Button
            variant="text"
            size="small"
            onClick={() => setCaseHistoryExpanded(!caseHistoryExpanded)}
            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
          >
            <Typography variant="body2" fontWeight={600}>
              {t('components.contactCenter.caseHistory')} ({caseHistory.length})
              {caseHistoryExpanded ? ' ▾' : ' ▸'}
            </Typography>
          </Button>
          {caseHistoryExpanded && (
            <Box
              sx={{
                mt: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: 'background.paper',
              }}
            >
              {caseHistory.map((entry, idx) => (
                <CaseHistoryRow
                  key={`${entry.caseId}-${entry.timestamp}-${idx}`}
                  entry={entry}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
